// Admin-side panel for managing live-chat conversations
import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { Send, MessageCircle, Sparkles, User as UserIcon, X, RotateCw } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 5000;

export default function AdminChatPanel() {
  const { lang, t } = useLang();
  const ct = t.chat;

  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null); // conversation object
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const r = await api.get("/chat/admin/conversations");
      setConversations(r.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConversation = useCallback(async (convId) => {
    try {
      const r = await api.get(`/chat/admin/conversations/${convId}`);
      setSelected(r.data.conversation);
      setMessages(r.data.messages || []);
      // Update list (clear unread)
      setConversations((prev) =>
        prev.map((c) => (c.conversation_id === convId ? { ...c, unread_admin: 0 } : c))
      );
    } catch {
      toast.error("Error loading conversation");
    }
  }, []);

  useEffect(() => {
    loadConversations();
    pollRef.current = setInterval(loadConversations, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [loadConversations]);

  useEffect(() => {
    if (!selected) return;
    const id = setInterval(() => loadConversation(selected.conversation_id), POLL_MS);
    return () => clearInterval(id);
  }, [selected?.conversation_id, loadConversation]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendReply = async (e) => {
    e?.preventDefault();
    if (!reply.trim() || !selected || sending) return;
    setSending(true);
    try {
      await api.post(`/chat/admin/conversations/${selected.conversation_id}/reply`, {
        text: reply.trim(),
      });
      setReply("");
      await loadConversation(selected.conversation_id);
      loadConversations();
    } catch {
      toast.error("Error sending reply");
    } finally {
      setSending(false);
    }
  };

  const closeConversation = async () => {
    if (!selected) return;
    try {
      await api.post(`/chat/admin/conversations/${selected.conversation_id}/close`);
      toast.success(ct.admin_closed_ok);
      await loadConversation(selected.conversation_id);
      loadConversations();
    } catch {
      toast.error("Error");
    }
  };

  const reopenConversation = async () => {
    if (!selected) return;
    try {
      await api.post(`/chat/admin/conversations/${selected.conversation_id}/reopen`);
      toast.success(ct.admin_reopened_ok);
      await loadConversation(selected.conversation_id);
      loadConversations();
    } catch {
      toast.error("Error");
    }
  };

  const formatTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(lang === "ar" ? "ar-DE" : lang, {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  return (
    <div className="border border-neutral-200 bg-white" data-testid="admin-chat-panel">
      <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3 bg-neutral-50">
        <div className="flex items-center gap-2">
          <MessageCircle size={18} className="text-[#FF5A00]" />
          <h2 className="text-base font-black tracking-tight">{ct.admin_title}</h2>
          <span className="text-xs text-neutral-500">({conversations.length})</span>
        </div>
        <button
          type="button"
          onClick={loadConversations}
          className="text-xs text-neutral-500 hover:text-[#FF5A00] flex items-center gap-1"
          data-testid="admin-chat-refresh"
        >
          <RotateCw size={12} />
          {ct.admin_refresh}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] min-h-[520px]">
        {/* Conversations list */}
        <div className="border-e border-neutral-200 overflow-y-auto max-h-[640px]">
          {loading && <div className="p-4 text-xs text-neutral-400">{t.common.loading}</div>}
          {!loading && conversations.length === 0 && (
            <div className="p-6 text-center text-xs text-neutral-400">{ct.admin_empty}</div>
          )}
          {conversations.map((c) => {
            const isSel = selected?.conversation_id === c.conversation_id;
            const isUnread = (c.unread_admin || 0) > 0;
            return (
              <button
                type="button"
                key={c.conversation_id}
                onClick={() => loadConversation(c.conversation_id)}
                className={`w-full text-start px-4 py-3 border-b border-neutral-100 transition ${
                  isSel ? "bg-[#FFF4ED] border-s-2 border-s-[#FF5A00]" : "hover:bg-neutral-50"
                }`}
                data-testid={`admin-chat-conv-${c.conversation_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className={`text-sm font-bold truncate ${isUnread ? "text-[#FF5A00]" : ""}`}>
                    {c.guest_name}
                  </div>
                  {isUnread && (
                    <span className="flex-shrink-0 bg-[#FF5A00] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {c.unread_admin}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-neutral-500 truncate mt-0.5">{c.guest_email}</div>
                <div className="text-[11px] text-neutral-600 truncate mt-1">{c.last_message_preview || "—"}</div>
                <div className="text-[10px] text-neutral-400 mt-1 flex items-center justify-between">
                  <span>{formatTime(c.last_message_at)}</span>
                  <span
                    className={`uppercase tracking-widest font-bold ${
                      c.status === "open" ? "text-emerald-600" : "text-neutral-400"
                    }`}
                  >
                    {c.status === "open" ? ct.admin_status_open : ct.admin_status_closed} · {c.lang.toUpperCase()}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Thread */}
        <div className="flex flex-col">
          {!selected && (
            <div className="flex-1 flex items-center justify-center text-sm text-neutral-400">
              {ct.admin_select}
            </div>
          )}
          {selected && (
            <>
              <div className="px-5 py-3 border-b border-neutral-200 bg-white flex items-center justify-between">
                <div>
                  <div className="font-black text-sm">{selected.guest_name}</div>
                  <div className="text-[11px] text-neutral-500">
                    {selected.guest_email} · {selected.lang.toUpperCase()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selected.status === "open" ? (
                    <button
                      type="button"
                      onClick={closeConversation}
                      className="text-xs px-3 py-1.5 border border-neutral-300 hover:bg-neutral-50 flex items-center gap-1"
                      data-testid="admin-chat-close-btn"
                    >
                      <X size={12} /> {ct.admin_close}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={reopenConversation}
                      className="text-xs px-3 py-1.5 border border-neutral-300 hover:bg-neutral-50"
                      data-testid="admin-chat-reopen-btn"
                    >
                      {ct.admin_reopen}
                    </button>
                  )}
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-neutral-50 max-h-[480px]">
                {messages.map((m) => {
                  const isUser = m.sender === "user";
                  const isAI = m.sender === "ai";
                  return (
                    <div
                      key={m.message_id}
                      className={`flex items-end gap-2 ${isUser ? "justify-start" : "justify-end"}`}
                    >
                      {isUser && (
                        <div className="w-7 h-7 rounded-full bg-neutral-300 flex items-center justify-center flex-shrink-0">
                          <UserIcon size={14} className="text-neutral-600" />
                        </div>
                      )}
                      <div className={`max-w-[75%] flex flex-col ${isUser ? "items-start" : "items-end"}`}>
                        <div
                          className={`px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                            isUser
                              ? "bg-white border border-neutral-200 rounded-2xl rounded-es-sm"
                              : isAI
                              ? "bg-gradient-to-br from-[#FF5A00] to-[#FF8A3D] text-white rounded-2xl rounded-ee-sm"
                              : "bg-[#090A0B] text-white rounded-2xl rounded-ee-sm"
                          }`}
                        >
                          {m.text}
                        </div>
                        <div className="text-[10px] text-neutral-400 mt-1 px-1">
                          {isAI ? ct.ai_label : isUser ? m.sender_name : ct.admin_you}
                          {" · "}
                          {formatTime(m.created_at)}
                        </div>
                      </div>
                      {!isUser && (
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-black ${
                            isAI ? "bg-gradient-to-br from-[#FF5A00] to-[#FF8A3D]" : "bg-[#090A0B]"
                          }`}
                        >
                          {isAI ? <Sparkles size={12} /> : "T"}
                        </div>
                      )}
                    </div>
                  );
                })}
                {messages.length === 0 && (
                  <div className="text-center text-xs text-neutral-400 py-8">—</div>
                )}
              </div>

              {selected.status === "open" && (
                <form onSubmit={sendReply} className="border-t border-neutral-200 p-3 flex gap-2 bg-white">
                  <input
                    type="text"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder={ct.admin_reply_placeholder}
                    className="flex-1 border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF5A00]"
                    disabled={sending}
                    data-testid="admin-chat-reply-input"
                  />
                  <button
                    type="submit"
                    disabled={sending || !reply.trim()}
                    className="bg-[#090A0B] text-white px-4 py-2 hover:bg-[#FF5A00] transition disabled:opacity-60 flex items-center justify-center"
                    data-testid="admin-chat-reply-btn"
                  >
                    <Send size={16} />
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
